import type {
  ActionsConfig,
  DeriveActionMap,
  ModuleDefinition,
  VersionString,
  ViewID,
} from "@bestowinc/tinkertoy-core";

import { submitModuleAction } from "./submit-module-action";

export type CreateClientActionMapOptions = {
  apiBaseUrl: string;
  /**
   * Exact module version the server pinned for the current view (matches
   * `loadedView.manifest.modules[moduleId].version`). Threaded through every
   * action call so the server can re-validate the request against the
   * manifest pinned for `viewId`.
   */
  moduleVersion: VersionString;
  /**
   * Identity of the view that produced the loader response which rendered the
   * module. Required so the server can confirm the module is part of the
   * view manifest before invoking the handler.
   */
  viewId: ViewID;
  viewVersion: VersionString;
  /**
   * Called after a successful action whose `shouldRevalidate` config is not
   * `false`. Typically wired to `useTinkertoy().revalidate()` so the current
   * view reloads server data.
   */
  revalidate?: () => void;
};

/**
 * Build the `actions` object passed to a remote module: each key POSTs to the
 * shell action endpoint with runtime validation and typed outputs.
 *
 * Identity (`viewId` + `viewVersion` + `moduleVersion`) is captured at map
 * construction time and forwarded on every call so the server can re-validate
 * the request against the manifest the loader pinned for the current view.
 * This means the map MUST be rebuilt whenever the loader's `loadedView`
 * changes — see {@link useClientActionMap}.
 */
export function createClientActionMap<TActions extends ActionsConfig>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FC<any> matches any module component variant
  definition: Pick<ModuleDefinition<any, TActions>, "id" | "actions">,
  { apiBaseUrl, moduleVersion, viewId, viewVersion, revalidate }: CreateClientActionMapOptions,
): DeriveActionMap<TActions> {
  const { id: moduleId, actions } = definition;
  const map: Record<string, (data: unknown) => Promise<unknown>> = {};

  for (const [actionName, actionEntry] of Object.entries(actions)) {
    map[actionName] = async (data: unknown) => {
      const result = await submitModuleAction({
        apiBaseUrl,
        moduleId,
        moduleVersion,
        viewId,
        viewVersion,
        actionName,
        data,
      });

      const shouldRevalidate = actionEntry.config?.shouldRevalidate ?? true;
      if (shouldRevalidate) {
        revalidate?.();
      }

      return result;
    };
  }

  return map as DeriveActionMap<TActions>;
}
